import { useEffect, useState } from 'react';
import { api } from './api.js';
import { Spinner } from './components.jsx';
import { prettySize } from './lib/media.js';

const when = (iso) => new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });

function Message({ m }) {
  const [open, setOpen] = useState(false);
  const long = m.text.length > 400 || m.text.split('\n').length > 8;
  const customer = m.sender === 'Customer';
  return (
    <div
      className={`rounded-xl border p-3 text-sm ${
        m.internal
          ? 'border-amber/60 bg-amber/10'
          : customer
            ? 'border-steel/20 bg-white dark:bg-charcoal/60 dark:border-white/10'
            : 'border-navy/20 bg-navy/5 dark:bg-steel/10 dark:border-white/10'
      }`}
    >
      <div className="mb-1 flex flex-wrap items-center gap-x-2 text-xs text-steel">
        <b className="text-charcoal dark:text-mist">{m.from}</b>
        <span>{customer ? 'customer' : 'Ascoat'}</span>
        {m.type !== 'email' && <span className="badge bg-steel/20 text-steel">{m.type === 'note' ? 'internal note' : m.type}</span>}
        <span>· {when(m.at)}</span>
      </div>
      <div className={`whitespace-pre-wrap break-words ${long && !open ? 'line-clamp-6' : ''}`}>{m.text || '(no text)'}</div>
      {long && (
        <button className="mt-1 text-steel underline" onClick={() => setOpen(!open)}>{open ? 'Show less' : 'Show more'}</button>
      )}
      {m.attachments.length > 0 && (
        <div className="mt-2 text-xs text-steel">
          Attachments: {m.attachments.map((a) => `${a.name}${a.bytes ? ` (${prettySize(a.bytes)})` : ''}`).join(', ')}
        </div>
      )}
    </div>
  );
}

function Thread({ jobId, ticket }) {
  const [internal, setInternal] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setData(null);
    setError(false);
    api.get(`/api/jobs/${jobId}/emails/${ticket.id}${internal ? '?internal=1' : ''}`)
      .then(setData)
      .catch(() => setError(true));
  }, [jobId, ticket.id, internal]);

  return (
    <div className="mt-3 space-y-2">
      <div className="flex items-center justify-between gap-3 text-sm">
        <label className="flex items-center gap-2">
          <input type="checkbox" className="h-5 w-5" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
          Show internal notes
        </label>
        <a className="text-steel underline" href={ticket.url} target="_blank" rel="noreferrer">Open in Zammad</a>
      </div>
      {!data && !error && <div className="flex justify-center py-3"><Spinner /></div>}
      {error && <p className="text-sm text-steel">Couldn't load this thread just now.</p>}
      {data?.messages.length === 0 && <p className="text-sm text-steel">No messages to show.</p>}
      {data?.messages.map((m) => <Message key={m.id} m={m} />)}
      {data && <p className="text-xs text-steel">Newest first. Quoted replies and signatures are trimmed – open Zammad for the full email.</p>}
    </div>
  );
}

// Staff level only. Tickets are matched on the job's SQ Number; tickets without one never show.
export default function EmailThreads({ job }) {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    api.get(`/api/jobs/${job.id}/emails`).then(setData).catch(() => setData({ error: true }));
  }, [job.id]);

  if (data?.configured === false) return null;

  return (
    <section className="space-y-3">
      <h2 className="label !mb-0">Emails</h2>
      {!data && <div className="flex justify-center py-4"><Spinner /></div>}
      {data?.error && <p className="text-sm text-steel">Couldn't load emails just now.</p>}
      {data?.tickets?.length === 0 && (
        <p className="text-sm text-steel">
          No email threads are linked to {job.jobNumber || 'this job'}. (A Zammad ticket needs its SQ Number field filled in.)
        </p>
      )}
      {data?.tickets?.map((t) => (
        <div key={t.id} className="card p-3">
          <button className="flex w-full items-start justify-between gap-3 text-left" onClick={() => setOpenId(openId === t.id ? null : t.id)}>
            <span className="min-w-0">
              <span className="block font-medium leading-snug">{t.title}</span>
              <span className="block text-xs text-steel">#{t.number} · {t.customer} · updated {when(t.updatedAt)}</span>
            </span>
            <span className="badge shrink-0 bg-steel/20 text-steel">{t.state}</span>
          </button>
          {openId === t.id && <Thread jobId={job.id} ticket={t} />}
        </div>
      ))}
    </section>
  );
}
